// Publications: attempt-resolution logic shared by App.js and Publications.jsx.
//
// A paper is stored as ONE inline "current" attempt plus a `history` array of
// previous attempts. The attempt that is displayed as current is not chosen by
// hand — it is always DERIVED as the "best" attempt in the pool (current +
// history) using a fixed status preference, then earliest submission date.
//
// `resolvePublication` normalizes a paper so the inline fields always hold the
// best attempt and `history` holds the rest (best-first). It is idempotent:
// resolving an already-resolved paper returns an equivalent paper, so it is
// safe to run on every load and after every edit.

// Higher preference first. A better status wins the "current" slot.
export const STATUS_PREFERENCE = [
  "published",
  "accepted",
  "drafting",
  "submitted",
  "awaiting-decision",
  "revision",
  "rejected",
];

// Fields that belong to a single submission attempt (they travel together when
// the current/history swap). Everything else on a paper is shared paper-level
// data (title, shortName, authors, comments, doiUrl, preprintUrl, ...).
export const ATTEMPT_KEYS = [
  "venueType",
  "conferenceName",
  "conferenceLocation",
  "conferenceDateFrom",
  "conferenceDateTo",
  "journalName",
  "publisher",
  "impactFactor",
  "quartile",
  "submittedDate",
  "expectedDecisionDate",
  "decisionDate",
  "cameraReadyDate",
  "publishedDate",
  "attemptNotes",
  "status",
  "revisionNumber",
];

const str = (v) => (v == null ? "" : String(v));
const orNull = (v) => (v == null || v === "" ? null : v);

// Pull the attempt-shaped subset out of a paper OR a history entry, defaulting
// every field so partial/legacy records are safe. `archivedAt` is carried for
// history entries (a live current attempt has none -> null).
export const extractAttempt = (src = {}) => ({
  venueType: src.venueType === "journal" ? "journal" : "conference",
  conferenceName: str(src.conferenceName),
  conferenceLocation: str(src.conferenceLocation),
  conferenceDateFrom: orNull(src.conferenceDateFrom),
  conferenceDateTo: orNull(src.conferenceDateTo),
  journalName: str(src.journalName),
  publisher: str(src.publisher),
  impactFactor: str(src.impactFactor),
  quartile: ["Q1", "Q2", "Q3", "Q4"].includes(src.quartile) ? src.quartile : "",
  submittedDate: orNull(src.submittedDate),
  expectedDecisionDate: orNull(src.expectedDecisionDate),
  decisionDate: orNull(src.decisionDate),
  cameraReadyDate: orNull(src.cameraReadyDate),
  publishedDate: orNull(src.publishedDate),
  attemptNotes: str(src.attemptNotes),
  status: STATUS_PREFERENCE.includes(src.status) ? src.status : "drafting",
  revisionNumber: str(src.revisionNumber),
  archivedAt: src.archivedAt || null,
});

const rank = (a) => {
  const i = STATUS_PREFERENCE.indexOf(a.status);
  return i === -1 ? STATUS_PREFERENCE.length : i;
};

// Best-first comparator: better status wins; then EARLIEST submission date
// (missing dates sort last); then stable/arbitrary (caller keeps input order).
export const compareAttempts = (a, b) => {
  const r = rank(a) - rank(b);
  if (r) return r;
  const ta = a.submittedDate ? Date.parse(a.submittedDate) : Infinity;
  const tb = b.submittedDate ? Date.parse(b.submittedDate) : Infinity;
  if (ta !== tb) return ta - tb;
  return 0;
};

// Turn an attempt back into a history entry (adds archivedAt if it lacks one).
const toHistoryEntry = (a) => {
  const entry = {};
  for (const k of ATTEMPT_KEYS) entry[k] = a[k];
  entry.archivedAt = a.archivedAt || new Date().toISOString();
  return entry;
};

// Normalize a paper so its inline attempt is the best one and `history` holds
// the rest (best-first). Paper-level fields are preserved untouched.
export const resolvePublication = (pub) => {
  if (!pub || typeof pub !== "object") return pub;

  const current = extractAttempt(pub);
  const hist = Array.isArray(pub.history)
    ? pub.history.map(extractAttempt)
    : [];

  // Stable sort: decorate with original index so ties keep input order (the
  // live current sits at index 0, so it wins pure ties -> idempotent).
  const pool = [current, ...hist]
    .map((a, i) => ({ a, i }))
    .sort((x, y) => compareAttempts(x.a, y.a) || x.i - y.i)
    .map((o) => o.a);

  const best = pool[0];
  const rest = pool.slice(1);

  // Strip the old inline attempt fields + history; keep all paper-level fields.
  const paper = { ...pub };
  delete paper.history;
  delete paper.archivedAt; // never a paper-level field
  for (const k of ATTEMPT_KEYS) delete paper[k];

  const inline = {};
  for (const k of ATTEMPT_KEYS) inline[k] = best[k];

  return {
    ...paper,
    ...inline,
    history: rest.map(toHistoryEntry),
  };
};
